import Icon from '../components/Icon'
import { tw } from './strings'
import { sv } from './servicesStrings'

export type Side = 'worker' | 'employer' | 'pro'

/**
 * Which side of the app you are looking at, and the way back to the splash.
 *
 * ── Why this stopped being two big buttons ─────────────────────────────────
 *
 * It was a full-width segmented control with "I'm Looking for Work" and "I'm
 * Hiring" in it, directly under the app bar. Those are the words for the
 * OPENING screen, where somebody is choosing for the first time and the choice
 * is the whole page. Repeated on every screen afterwards they are two large
 * slabs of chrome above the content, restating a decision that was already
 * made — and on a phone they cost the first job card its place on the fold.
 *
 * A signed-in person needs to KNOW which side they are on and be able to
 * change it. That is a line of text, not a pair of buttons.
 *
 * ── Only for hybrid accounts ───────────────────────────────────────────────
 *
 * An account has one role, picked at the opening screen, and shows no switch at
 * all. The control appears only once a second role has been added on purpose
 * (Profile > Use this account another way), and then lists only roles the
 * account holds.
 */
export default function SideSwitch({
  side,
  roles,
  onChoose,
}: {
  side: Side
  /** The roles this account actually holds. Rendered only when there is more than one. */
  roles: Side[]
  onChoose: (next: Side) => void
}) {
  // One link to each OTHER role the account holds, and never to one it does not.
  const others = roles.filter((s) => s !== side)
  const nowLabel = { worker: tw('w.switch.working'), employer: tw('w.switch.hiring'), pro: sv('sv.switch.now') }[side]
  const toLabel = { worker: tw('w.switch.toWorking'), employer: tw('w.switch.toHiring'), pro: sv('sv.switch.to') }

  return (
    <div className="w-switch">
      <span className="w-switch-now">{nowLabel}</span>

      {others.map((other) => (
        <button key={other} className="w-switch-link" onClick={() => onChoose(other)}>
          <Icon name="switch" size={15} />
          {/* Names the destination, not the action. "Switch" alone makes
              somebody work out which of the states they would land in. */}
          {toLabel[other]}
        </button>
      ))}
    </div>
  )
}
